import { AppBar, Button, Dialog, DialogActions, DialogContent, FormControl, FormHelperText, IconButton, Switch, TextField, Toolbar, Tooltip, Typography } from '@material-ui/core';
import React from 'react';
import { Transitions } from '../../../../customComponents/animations/Transitions';
import { ITrapType } from '../../../../../types/model/masterData/trap/trapType';
import { Add, Close, Delete, Save } from '@material-ui/icons';
import GeneralFunctions from '../../../../../store/general/functions';
import InsectDropdown from '../../../../customComponents/dropdowns/InsectDropdown';
import { IInsect } from '../../../../../types/model/masterData/insect';
import StandardFab from '../../../../customComponents/button/StandardFab';
import TrapTypeFunctions from '../../../../../store/masterData/trap/trapType/functions';

interface ITrapTypeEditDialogProps {
    value : ITrapType | null;

    isLoading ?: boolean;

    onClose ?: () => void;
}

interface ITrapTypeEditDialogState {
    name : string;
    isActive : boolean;
    insects : Array<string>;
}

export default class TrapTypeEditDialog extends React.PureComponent<ITrapTypeEditDialogProps, ITrapTypeEditDialogState> {
    constructor(props : ITrapTypeEditDialogProps) {
        super(props);
        this.state = {
            name: '',
            isActive: true,
            insects: [],
        };
    }

    public componentDidUpdate(prevProps : Readonly<ITrapTypeEditDialogProps>) : void {
        if (!prevProps.value && this.props.value) {
            this.setState({
                insects: this.props.value.insects,
                name: this.props.value.name,
                isActive: this.props.value.isActive,
            });
        }
    }

    private readonly onNameChange = (e : React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.setState({
            name: e.target.value,
        });
    };

    private readonly save = async () => {
        try {
            const {
                value,
            } = this.props;
            if (!value) return;
    
            const {
                name,
                isActive,
                insects,
            } = this.state;

            if (!name) return;
            if (insects.some(x => !x)) return;
    
            await TrapTypeFunctions.save({
                ...value,
                name,
                isActive,
                insects,
            });

            if (this.props.onClose) this.props.onClose();
            GeneralFunctions.generalShowSuccessSnackbar('Saved');
        } catch (ex) {
            GeneralFunctions.generalShowErrorSnackbar('Error saving trap type', ex);
        }
    };

    private readonly onSaveClick = () => {
        this.save();
    };

    private readonly onIsActiveChange = () => {
        this.setState({
            isActive: !this.state.isActive,
        });
    };

    private readonly onAddClick = () => {
        const insects = this.state.insects.slice();

        insects.push('');

        this.setState({
            insects,
        });
    };
    
    private readonly onInsectChange = (name ?: string, value ?: IInsect) => {
        if (!name) return;
        const [ indexString ] = name.split('.');
        const index = Number(indexString);
        const insects = this.state.insects.slice();
        insects.splice(index, 1, value?.id ?? '');

        this.setState({
            insects,
        });
    };
    
    private readonly onDeleteClick = (event : React.MouseEvent<HTMLButtonElement>) => {
        const index = Number(event.currentTarget.value);
        const insects = this.state.insects.slice();
        insects.splice(index, 1);

        this.setState({
            insects,
        });
    };

    public render = () => {
        const {
            value,
            isLoading,
            onClose,
        } = this.props;

        const {
            name,
            isActive,
            insects,
        } = this.state;

        return (
            <Dialog
                open={!!value}
                TransitionComponent={Transitions.Down}
                transitionDuration={500}
                maxWidth={'md'}
                fullWidth
                aria-labelledby='edit-trap-type-dialog-title'
                aria-describedby='edit-trap-type-description'>
                <AppBar className='fdr posr aic jcc bcpd' position='static'>
                    <Toolbar className={'fdr flx1 aic jcc'}>
                        <Typography>
                            {value?.id ? 'EDIT' : 'ADD'} TRAP TYPE
                        </Typography>
                        <span className='flx1' />
                        <Tooltip title='Close'>
                            <div>
                                <IconButton color='inherit' onClick={onClose} aria-label='Close'>
                                    <Close />
                                </IconButton>
                            </div>
                        </Tooltip>
                    </Toolbar>
                </AppBar>
                <DialogContent className='fdc flx1 hfill'>
                    <div className={'fdr ais jcfs'}>
                        <div className={'fdc flx1 jcfs p5 mb10 pr20'} style={{ marginTop: '16px' }}>
                            <FormControl fullWidth>
                                <TextField
                                    autoComplete='off'
                                    required
                                    error={!name}
                                    autoFocus
                                    margin='dense'
                                    id='name'
                                    label='Name'
                                    type='text'
                                    fullWidth
                                    value={name}
                                    onChange={this.onNameChange}
                                    disabled={isLoading}
                                />
                                {
                                    !name &&
                                    <FormHelperText error>Required</FormHelperText>
                                }
                            </FormControl>
                        </div>
                        <div className={'fdr flx1 aic p5 mb10 pr20'} style={{ marginTop: '16px' }}>
                            <Typography className='pr6' >Is Active?</Typography>
                            <Switch
                                id={'isActive'}
                                name={'isActive'}
                                checked={isActive}
                                color='primary'
                                onChange={this.onIsActiveChange}
                                inputProps={{
                                    'aria-label': 'Is Active',
                                    id: 'isActive',
                                }}
                                disabled={isLoading}
                            />
                        </div>
                    </div>
                    <div className={'fdc flx1 p5 mb10'}>
                        <Toolbar>
                            <Typography variant='h5' color='inherit'>
                                INSECTS
                            </Typography>
                            <span className={'flx1'} />
                            <StandardFab aria-label='add' disabled={isLoading} onClick={this.onAddClick}>
                                <Add />
                            </StandardFab>
                        </Toolbar>
                        {
                            insects.map((x, i) => (

                                <div key={i} className='fdr ais jcfs mt7'>
                                    <div className='fdc flx1 mr15'>
                                        <InsectDropdown
                                            key={`${i}`}
                                            required
                                            id={`${i}.name`}
                                            name={`${i}.name`}
                                            fullWidth
                                            value={x}
                                            onChange={this.onInsectChange}
                                            disabled={isLoading}
                                        />
                                    </div>
                                    <div className='fdr aic'>
                                        <IconButton disabled={isLoading} className='cpr' size='small' value={i} onClick={this.onDeleteClick}>
                                            <Delete />
                                        </IconButton>
                                    </div>
                                </div>
                            ))
                        }
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button color='primary' disabled={isLoading} onClick={this.onSaveClick}>
                        <Save style={{ marginRight: '10px' }} />
                        SUBMIT
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };
}
